import * as React from "react";

import { cn } from "@/shared/lib/utils";

export interface ColocaPageHeaderProps extends Omit<React.ComponentPropsWithoutRef<"header">, "title"> {
  /** Page title — rendered as the page `<h1>`. */
  title: React.ReactNode;
  /** One-line description of what the page manages. Omit when the title is self-explanatory. */
  description?: React.ReactNode;
  /**
   * Primary and secondary actions, right-aligned on desktop and stacked under the title on mobile. Order
   * buttons by increasing importance: outline/ghost first, the single primary action last.
   */
  actions?: React.ReactNode;
}

/**
 * Standard page header for dashboard views: `<h1>` title, supporting description, and a right-aligned actions
 * slot. Keeps the title/action layout consistent across modules and collapses gracefully on small screens.
 *
 * Breadcrumbs are not part of this component — they live in the app shell via `useAppShellBreadcrumb`.
 *
 * @summary page title + description + right-aligned actions slot
 */
export const ColocaPageHeader = React.forwardRef<HTMLElement, ColocaPageHeaderProps>(
  function ColocaPageHeader({ title, description, actions, className, children, ...rest }, ref) {
    return (
      <header
        ref={ref}
        className={cn("flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between", className)}
        {...rest}
      >
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">{title}</h1>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
          {children}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </header>
    );
  },
);
